<template>
  <div class="group-add-operators-container">
    <!-- Empty State: Show only when no assigned operators -->
    <div v-if="assignedOperators.length === 0 && !loadingAssigned" class="empty-state-wrapper">
      <EmptyStateButton 
        :items="[]"
        :message="'The list of assigned operators will be shown here.'"
        :title="'Operators'"
        :buttonText="'Assign Operators'"
        @create="openOperatorDialog"
        :loading="updatingGroup"
      />
    </div>

    <!-- Assigned Operators Table: Show only when there are assigned operators -->
    <div class="assigned-operators-section" v-if="assignedOperators.length > 0">
      <v-card flat>
        <v-card-title class="pb-2">
        </v-card-title>
        <v-card-text class="pa-0">
          <v-data-table
            :headers="assignedOperatorsHeaders"
            :items="assignedOperators"
            :loading="loadingAssigned"
            hide-default-footer
            disable-pagination
            dense
            class="assigned-operators-table"
          >
            <template slot="item.name" slot-scope="{ item }">
              <div class="d-flex align-center">
                <v-avatar size="32" class="mr-3">
                  <v-icon color="primary" size="20">mdi-account</v-icon>
                </v-avatar>
                <div>
                  <div class="text-body-2 font-weight-medium">
                    {{ item.first_name }} {{ item.last_name }}
                  </div>
                  <div class="text-caption grey--text">{{ item.email }}</div>
                </div>
              </div>
            </template>

            <template slot="item.gender" slot-scope="{ item }">
              <span class="text-body-2">{{ item.gender || '-' }}</span>
            </template>

            <template slot="item.status" slot-scope="{ item }">
              <v-chip 
                x-small 
                :color="item.enabled ? 'success' : 'error'" 
                text-color="white"
              >
                {{ item.enabled ? 'Active' : 'Inactive' }}
              </v-chip>
            </template>

            <template slot="item.tags" slot-scope="{ item }">
              <div v-if="item.tags && item.tags.length > 0">
                <v-chip 
                  v-for="tag in item.tags.slice(0, 2)" 
                  :key="tag" 
                  x-small 
                  class="mr-1"
                >
                  {{ tag }}
                </v-chip>
                <span v-if="item.tags.length > 2" class="text-caption grey--text">
                  +{{ item.tags.length - 2 }}
                </span>
              </div>
              <span v-else class="text-body-2">-</span>
            </template>

            <template slot="item.locations" slot-scope="{ item }">
              <div v-if="item.locations && item.locations.length > 0">
                <span class="text-body-2">{{ item.locations.map(l => l.name || l).join(', ').substring(0, 30) }}</span>
                <span v-if="item.locations.length > 1" class="text-caption grey--text">...</span>
              </div>
              <span v-else class="text-body-2">-</span>
            </template>
            
            <template slot="item.actions" slot-scope="{ item }">
              <v-btn
                icon
                x-small
                color="error"
                @click="handleRemoveOperatorFromGroup(item)"
                :disabled="updatingGroup"
              >
                <v-icon size="16">mdi-delete</v-icon>
              </v-btn>
            </template>
          </v-data-table>
        </v-card-text>
        <v-card-actions class="justify-end px-0 py-6">
          <v-btn
            color="primary"
            @click="openOperatorDialog"
            :loading="updatingGroup"
            class="text-capitalize"
          >Add Operators
          </v-btn>
        </v-card-actions>
      </v-card>
    </div>

    <!-- Operator Selection Dialog -->
    <v-dialog v-model="operatorDialog" max-width="600px" persistent>
      <v-card class="pa-6">
        <v-card-title>
          <span class="text-h6">Select Operators</span>
          <v-spacer />
          <v-btn icon @click="closeOperatorDialog">
            <v-icon>{{ icons.mdiClose }}</v-icon>
          </v-btn>
        </v-card-title>
        
        <v-card-text>
          <!-- Search field -->
          <v-text-field
            v-model="searchOperators"
            label="Search operators..."
            placeholder="Search by name, email, or username"
            outlined
            dense
            prepend-inner-icon="mdi-magnify"
            clearable
            class="mb-3"
          />
          
          <!-- Operators list with checkboxes -->
          <div class="operators-selection-list">
            <v-list dense max-height="400" class="overflow-y-auto">
              <v-list-item
                v-for="operator in filteredAvailableOperators"
                :key="operator.uuid"
                @click="toggleOperator(operator)"
                class="operator-select-item"
              >
                <v-list-item-action>
                  <v-checkbox
                    :input-value="isOperatorSelected(operator)"
                    @change="toggleOperator(operator)"
                    @click.stop
                    color="primary"
                    class="ma-0 pa-0"
                  />
                </v-list-item-action>
                <v-list-item-content>
                  <v-list-item-title class="text-body-2">
                    {{ operator.first_name }} {{ operator.last_name }}
                  </v-list-item-title>
                  <v-list-item-subtitle class="text-caption">{{ operator.email }}</v-list-item-subtitle>
                </v-list-item-content>
              </v-list-item>
              <v-list-item v-if="filteredAvailableOperators.length === 0">
                <v-list-item-content>
                  <v-list-item-title class="text-center text-body-2 grey--text">
                    {{ searchOperators ? `No operators found matching "${searchOperators}"` : 'No operators available' }}
                  </v-list-item-title>
                </v-list-item-content>
              </v-list-item>
            </v-list>
          </div>
        </v-card-text>
        
        <v-card-actions>
          <v-chip 
            v-if="tempSelectedOperators.length > 0"
            small 
            color="primary" 
            class="ml-2"
          >
            {{ tempSelectedOperators.length }} selected
          </v-chip>
          <v-spacer />
          <v-btn class="text-capitalize" text @click="closeOperatorDialog">
            Cancel
          </v-btn>
          <v-btn 
            color="primary" 
            @click="confirmOperatorSelection"
            :loading="updatingGroup"
            class="text-capitalize"
          >
            Save Selection
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapActions } from "vuex";
import EmptyStateButton from "./EmptyStateButton.vue";
import { mdiAccountPlus, mdiClose, mdiAccountCheck, mdiAccount, mdiCloseCircle, mdiDelete } from "@mdi/js";

export default {
  name: "GroupAddOperators",
  components: {
    EmptyStateButton
  },
  props: {
    form: {
      type: Object,
      default: () => ({ selectedOperators: [] })
    },
    createdGroupId: {
      type: String,
      default: null
    }
  },
  data() {
    return {
      localForm: { ...this.form },
      searchOperators: "",
      availableOperators: [],
      operatorDialog: false,
      tempSelectedOperators: [], // Temporary selection while dialog is open
      updatingGroup: false,
      assignedOperators: [],
      assignedOperatorsHeaders: [
        { text: 'Name', value: 'name', sortable: false },
        { text: 'Gender', value: 'gender', sortable: false },
        { text: 'Status', value: 'status', sortable: false },
        { text: 'Tags', value: 'tags', sortable: false },
        { text: 'Locations', value: 'locations', sortable: false },
        { text: 'Actions', value: 'actions', sortable: false, align: 'end' }
      ],
      loadingAssigned: false,
      icons: {
        mdiAccountPlus: mdiAccountPlus,
        mdiClose: mdiClose,
        mdiAccountCheck: mdiAccountCheck,
        mdiAccount: mdiAccount,
        mdiCloseCircle: mdiCloseCircle,
        mdiDelete: mdiDelete
      }
    };
  },
  computed: {
    filteredAvailableOperators() {
      if (!this.searchOperators) return this.availableOperators;
      
      const search = this.searchOperators.toLowerCase();
      return this.availableOperators.filter(op => 
        `${op.first_name} ${op.last_name}`.toLowerCase().includes(search) ||
        op.email.toLowerCase().includes(search) ||
        (op.username && op.username.toLowerCase().includes(search))
      );
    },
  },
  methods: {
    ...mapActions({
      fetchOperators: "operators/fetchOperators",
      addOperatorsToGroup: "operators/addOperatorsToGroup",
      fetchOperatorsInGroup: "operators/fetchOperatorsInGroup",
      removeOperatorFromGroup: "operators/removeOperatorFromGroup",
      addAlert: "global/addAlert"
    }),
    emitChange() {
      // Ensure name is preserved when emitting changes
      if (!this.localForm.name || this.localForm.name.trim() === "") {
        // Try to recover name from parent form if possible
        if (this.form && this.form.name) {
          this.localForm.name = this.form.name;
        }
        // Log error for debugging
        console.error("[GroupAddOperators] emitChange: 'name' field is missing from localForm.", this.localForm);
        this.$emit("error", "Group name is missing from form. Please check your input.");
      }
      this.$emit("update:form", this.localForm);
    },
    
    // Dialog methods
    openOperatorDialog() {
      // Initialize temporary selection with current selection
      this.tempSelectedOperators = [...this.localForm.selectedOperators];
      this.operatorDialog = true;
      this.searchOperators = ""; // Clear search when opening
    },
    
    closeOperatorDialog() {
      this.operatorDialog = false;
      this.tempSelectedOperators = [];
      this.searchOperators = "";
    },
    
    confirmOperatorSelection() {
      // Only allow saving if at least one operator is selected
      if (!this.tempSelectedOperators || this.tempSelectedOperators.length === 0) {
        this.$emit("error", "Please select at least one operator to assign to the group.");
        return;
      }
      // Apply temporary selection to actual form
      this.localForm.selectedOperators = [...this.tempSelectedOperators];
      this.emitChange();
      // If we have a created group, update it with the selected operators
      if (this.createdGroupId) {
        this.updateGroupWithOperators().then(() => {
          this.addAlert({ type: "success", message: "Operators successfully assigned to group." });
        }).catch(error => {
          this.addAlert({ type: "error", message: `Error assigning operators to group: ${error.message || error}` });
        });
      } else {
        this.closeOperatorDialog();
      }
    },
    
    // Checkbox toggle methods
    isOperatorSelected(operator) {
      return this.tempSelectedOperators.some(op => op.uuid === operator.uuid);
    },
    
    toggleOperator(operator) {
      const existingIndex = this.tempSelectedOperators.findIndex(op => op.uuid === operator.uuid);
      
      if (existingIndex > -1) {
        // Remove operator
        this.tempSelectedOperators.splice(existingIndex, 1);
      } else {
        // Add operator
        this.tempSelectedOperators.push(operator);
      }
    },
    
    // Group update methods
    async updateGroupWithOperators() {
      if (!this.createdGroupId) return;
      // Validate that we have operators selected
      if (!this.localForm.selectedOperators || this.localForm.selectedOperators.length === 0) {
        this.$emit("error", "Please select at least one operator to assign to the group.");
        return;
      }
      this.updatingGroup = true;
      try {
        // Get user email from accounts store getter
        const userEmail = this.$store.getters['accounts/getUser']?.email || this.$store.getters['accounts/getUser']?.user?.email || '';
        if (!userEmail || userEmail.trim() === '') {
          throw new Error("Unable to identify the current user. Please try logging out and logging back in.");
        }
        // Prepare the payload with only UUIDs and user info
        const payload = {
          created_by_email: userEmail.trim(),
          operator_uuids: this.localForm.selectedOperators.map(op => op.uuid)
        };
        await this.addOperatorsToGroup({
          operator_group_uuid: this.createdGroupId,
          space_operators: payload
        });
        // Refresh assigned operators list
        await this.fetchAssignedOperators();
        this.closeOperatorDialog();
      } catch (error) {
        console.error("Error adding operators to group:", error);
        this.addAlert({ type: "error", message: `Error adding operators to group: ${error.message || error}` });
      } finally {
        this.updatingGroup = false;
      }
    },
    
    // Fetch assigned operators
    async fetchAssignedOperators() {
      if (!this.createdGroupId) return;
      
      this.loadingAssigned = true;
      try {
   
        
        const operators = await this.fetchOperatorsInGroup({
          operator_group_uuid: this.createdGroupId,
          qs: { page_size: 100 } // Get all operators
        });
        
        this.assignedOperators = operators || [];
  
        
      } catch (error) {
        this.addAlert({ type: "error", message: `Error fetching assigned operators: ${error.message || error}` });
      } finally {
        this.loadingAssigned = false;
      }
    },
    
    // Remove operator from group
    async handleRemoveOperatorFromGroup(operator) {
      if (!this.createdGroupId) return;
      
      this.updatingGroup = true;
      try {
        await this.removeOperatorFromGroup({
          operator_group_uuid: this.createdGroupId,
          operator_uuid: operator.uuid
        });

        
        // Refresh assigned operators list
        await this.fetchAssignedOperators();
        
        // Also remove from local form if present
        this.localForm.selectedOperators = this.localForm.selectedOperators.filter(
          op => op.uuid !== operator.uuid
        );
        this.emitChange();
        
      } catch (error) {
        this.addAlert({ type: "error", message: `Error removing operator: ${error.message || error}` });
      } finally {
        this.updatingGroup = false;
      }
    },
    
    validate() {
      // Operators are optional for groups, so always return true
      return true;
    },
  },
  async mounted() {
    // Fetch available operators for selection
    try {
      this.availableOperators = await this.fetchOperators("page_size=100&page=1");
    } catch (error) {
      this.addAlert({ type: "error", message: `Error loading operators: ${error.message || error}` });
    }
    
    // Fetch assigned operators if group exists
    if (this.createdGroupId) {
      await this.fetchAssignedOperators();
    }
  },
  watch: {
    createdGroupId: {
      handler(newVal) {
        if (newVal) {
          this.fetchAssignedOperators();
        }
      },
      immediate: true
    }
  }
};
</script>

<style scoped>
.group-add-operators-container {
  position: relative;
  min-height: 70vh;
  width: 100%;
}

.empty-state-wrapper {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 100%;
  max-width: 400px;
  text-align: center;
}

/* Assigned operators table */
.assigned-operators-table {
  border: 1px solid var(--v-borderColor-base, rgba(0,0,0,0.12));
  border-radius: 4px;
}

.assigned-operators-table .v-data-table__wrapper {
  border-radius: 4px;
}

/* Dark theme support for table */
.theme--dark .assigned-operators-table {
  border-color: rgba(255,255,255,0.12);
}
</style>
